import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  ListItem,
  Link,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AttachmentIcon from '@mui/icons-material/AttachFile'; // Vertical attachment icon
import AssignmentIcon from '@mui/icons-material/Assignment'; // Assignment icon for minutes
import axios from 'axios';

const MeetingList = (props) => {
  const { loading, meetings, year, searchKeyword, error } = props;
  const [expanded, setExpanded] = useState(null);
  const [meetingData, setMeetingData] = useState(meetings);

  const handleExpand = (id) => {
    setExpanded((prev) => (prev === id ? null : id));
  };
  const handleFetchPreview = async (fileId) => {
    try {
  
      const response = await axios.get(`//sire.bostonplans.org/api/preview/${fileId}`);
      const fileUrl = response.data || "No Preview Available";
  
      if (fileUrl !== "No Preview Available") {
        window.open(fileUrl, "_blank"); // Open in new tab
      } else {
        alert("No preview available for this document.");
      }
    } catch (error) {
      alert("Failed to fetch preview link.");
    }
  };
  // Show loading message if data is still being fetched
  if (loading) {
    return <Typography>Loading meeting data...</Typography>;
  }

  // Show error message if there was an issue fetching data
  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  // Ensure meetings exist before rendering
  if (!meetings || meetings.length === 0) {
    return <Typography>No meetings available.</Typography>;
  }
// Step 1: Create a new structure to track where minutes should be placed
const updatedMeetings = {};

meetings.forEach((meeting) => {
  let meetingDate = new Date(meeting.title);
  if (isNaN(meetingDate)) {
    return;
  }

  const meetingKey = meeting.title; // Store by formatted date
  if (!updatedMeetings[meetingKey]) {
    updatedMeetings[meetingKey] = { ...meeting, items: [] };
  }

  meeting.items.forEach((item) => {
    const docType = item.metadataDetails?.documentType?.trim().toLowerCase();

    if (docType === "minutes") {
      // Step 2: Determine the previous month & year
      let prevMonth = meetingDate.getMonth() - 1;
      let prevYear = meetingDate.getFullYear();

      if (prevMonth < 0) {
        prevMonth = 11; // Move to December of previous year
        prevYear -= 1;
      }

      // Step 3: Find the most recent meeting in the previous month
      const previousMeetings = meetings.filter((m) => {
        const mDate = new Date(m.title);
        return mDate.getFullYear() === prevYear && mDate.getMonth() === prevMonth;
      });

      if (previousMeetings.length > 0) {
        // Get the most recent meeting
        const latestPrevMeeting = previousMeetings.reduce((latest, m) =>
          new Date(m.title) > new Date(latest.title) ? m : latest
        );

        const prevKey = latestPrevMeeting.title;
        if (!updatedMeetings[prevKey]) {
          updatedMeetings[prevKey] = { ...latestPrevMeeting, items: [] };
        }

        // Move "Minutes" document to previous month's meeting
        updatedMeetings[prevKey].items.push(item);
      } else {
        console.warn(`No previous month meeting found for Minutes document: ${item.name}`);
      }
    } else {
      // Keep all other documents in the current meeting
      updatedMeetings[meetingKey].items.push(item);
    }
  });
});

// Convert object back into sorted array
const sortedMeetings = Object.values(updatedMeetings).sort((a, b) => new Date(b.title) - new Date(a.title));

  return (
    <div className="meeting-list">
      <h2>Meetings {searchKeyword ? `for "${searchKeyword}"` : `for ${year}`}</h2>
      {sortedMeetings.map((meeting, index) => {
        
        // Process and sort items
        const sortedItems = [...meeting.items]
          .map((item) => {
            let displayName;
            const docType = item.metadataDetails?.documentType?.trim().toLowerCase();
            const language = item.metadataDetails?.language;

            // Apply naming logic based on metadataDetails
            if (language) {
              displayName = `Agenda - ${language}`; // Language takes priority
            } else if (docType?.includes('approved')) {
              displayName = 'Agenda - Approved';
            } else if (docType?.includes('agenda')) {  // Ensure "agenda" is detected
              displayName = 'Agenda';
            } else if (docType?.includes('minutes')) {  // Check for "minutes"
              displayName = 'Minutes';
            } else {
              let originalName = item.name;
              let newdisplayName = originalName;
              // Regex to match the pattern: "YYYY-MM-DD_EDIC_xx_*" or "YYYY-MM-DD_BRA_xx_*"
              const regex = /^(\d{4}-\d{2}-\d{2})_(EDIC_\d{2}|BRA_\d{2})_(.+)(\.\w+)$/i;
              const match = originalName.match(regex);
    
              if (match) {
                const [, , code, restOfName, extension] = match;
                const formattedExtension = extension.replace('.', '').toUpperCase(); // Remove dot and convert to uppercase
                let formattedName = restOfName.replace(/_/g, ' '); // Replace underscores with spaces
                newdisplayName = `[${code}] ${formattedName} [${formattedExtension}]`;
              } else {
                newdisplayName = originalName.replace(/_/g, ' '); // Replace underscores if no match
              }
              displayName = newdisplayName
            }

            return { ...item, displayName };
          })
          .sort((a, b) => {
            const nameA = a.displayName.trim() || '';
            const nameB = b.displayName.trim() || '';
          
            // Define priority levels
            const priorityMap = {
              'agenda': 1,
              'agenda - approved': 2,
              'agenda -': 3, // Any agenda-related names
              'minutes': 4,
            };
          
            // Get priority based on display name
            const getPriority = (name) => {
              const lowerName = name.toLowerCase();
              if (lowerName === 'agenda') return priorityMap['agenda'];
              if (lowerName === 'agenda - approved') return priorityMap['agenda - approved'];
              if (lowerName.startsWith('agenda -')) return priorityMap['agenda -'];
              if (lowerName === 'minutes') return priorityMap['minutes'];
              return 999; // Default priority for other names (sorted alphabetically)
            };
          
            const priorityA = getPriority(nameA);
            const priorityB = getPriority(nameB);
          
            // Sort by priority first
            if (priorityA !== priorityB) {
              return priorityA - priorityB;
            }
          
            // Default alphabetical sorting
            return nameA.localeCompare(nameB);
          });
          

        return (
          <Accordion
            key={index}
            expanded={expanded === index}
            onChange={() => handleExpand(index)}
            className="accordion"
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon className="expand-icon" />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
              className={`accordion-summary ${expanded === index ? 'expanded' : ''}`}
            >
              <div className="accordion-header">
                <div className="calendar-container">
                  <CalendarTodayIcon className="calendar-icon" />
                  <div className="badge">{meeting.items.length}</div>
                  <Typography className="meeting-title">{meeting.title}</Typography>
                </div>
              </div>
            </AccordionSummary>
            <AccordionDetails className="accordion-details">
              <List>
                {sortedItems.map((item, idx) => {
                  const originalName = item.name.toLowerCase();
                  const displayName = item.displayName;

                  const hasFileIcon = displayName.toLowerCase().includes('agenda');
                  const hasAttachmentIcon = originalName.includes('.pdf');
                  const hasMinutesIcon = displayName.toLowerCase().includes('minutes');

                  // Priority: File icon first, then Minutes icon, then Attachment icon
                  const IconComponent = hasFileIcon
                    ? InsertDriveFileIcon
                    : hasMinutesIcon
                    ? AssignmentIcon // Using the Assignment icon for Minutes
                    : hasAttachmentIcon
                    ? AttachmentIcon
                    : null;

                  return (
                    <ListItem key={idx} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                      {/* File Name with Highest Priority Icon */}
                      <Box display="flex" alignItems="center">
                        {IconComponent && <IconComponent sx={{ mr: 1 }} />}
                        <Link
                          href={item.url !== "No Shared Link Available" ? item.url : "javascript:void(0)"}
                          target={item.url !== "No Shared Link Available" ? "_blank" : ""}
                          rel="noopener noreferrer"
                          underline="hover"
                          onClick={(e) => {
                            if (item.url === "No Shared Link Available") {
                              e.preventDefault();
                              handleFetchPreview(item.id);
                            }
                          }}
                        >
                          {displayName}
                        </Link>
                      </Box>
                      {/* Organization as a Tag */}
                      <Box 
                        sx={{ 
                          mt: 0.5, 
                          px: 1.5, 
                          py: 0.5, 
                          bgcolor: 'grey.200', 
                          borderRadius: 1, 
                          fontSize: '0.875rem', 
                          display: 'inline-block' 
                        }}
                      >
                        {item.organization}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
};

export default MeetingList;
